import type { TimeSeriesPoint } from "@/lib/validation";

export type LineSeriesTone = "sky" | "emerald" | "amber" | "red" | "slate";

export interface LineSeries {
  name: string;
  tone: LineSeriesTone;
  points: TimeSeriesPoint[];
}

interface MultiLineChartProps {
  series: LineSeries[];
  height?: number;
  emptyMessage?: string;
}

// Polyline strokes pick up `currentColor` from the surrounding text colour,
// which is set per-series via the token-driven utility classes below. This
// keeps theme switching automatic (no inline-style HEX maps needed).
const SERIES_COLOR_CLASS: Record<LineSeriesTone, string> = {
  sky: "text-line-sky",
  emerald: "text-line-emerald",
  amber: "text-line-amber",
  red: "text-line-red",
  slate: "text-line-slate",
};

const LEGEND_DOT: Record<LineSeriesTone, string> = {
  sky: "bg-line-sky",
  emerald: "bg-line-emerald",
  amber: "bg-line-amber",
  red: "bg-line-red",
  slate: "bg-line-slate",
};

const VIEW_WIDTH = 600;

export function MultiLineChart({
  series,
  height = 160,
  emptyMessage = "No data in the selected range",
}: MultiLineChartProps) {
  const hasData = series.some((s) => s.points.length > 0);

  if (!hasData) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-border-subtle text-sm text-fg-faint"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const allPoints = series.flatMap((s) => s.points);
  const xValues = allPoints.map((p) => new Date(p.bucket).getTime());
  const yValues = allPoints.map((p) => p.value);

  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = 0;
  const yMaxRaw = Math.max(...yValues, 1);
  const yMax = yMaxRaw === 0 ? 1 : yMaxRaw;

  const xSpan = xMax - xMin || 1;
  const padding = { top: 12, right: 8, bottom: 8, left: 8 };
  const innerW = VIEW_WIDTH - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  // ~5 evenly-spaced date ticks for the x-axis timeline + a peak label.
  const tickCount = 5;
  const sameYear = new Date(xMin).getFullYear() === new Date(xMax).getFullYear();
  const fmtTick = (t: number) =>
    new Date(t).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "2-digit" }),
    });
  const xTicks = Array.from({ length: tickCount }, (_, i) => ({
    label: fmtTick(xMin + (xSpan * i) / (tickCount - 1)),
    pctLeft: (i / (tickCount - 1)) * 100,
  }));
  const yMaxLabel = Math.round(yMaxRaw).toLocaleString("en-US");

  const projectX = (t: number) =>
    padding.left + ((t - xMin) / xSpan) * innerW;
  const projectY = (v: number) =>
    padding.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const gridYTicks = 4;

  return (
    <div className="flex flex-col gap-1.5">
      {/* peak (y-max) reference so the chart scale is legible */}
      <div className="flex justify-end text-[10px] text-fg-faint">
        peak {yMaxLabel}
      </div>
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        className="w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Time-series chart"
      >
        {Array.from({ length: gridYTicks + 1 }).map((_, i) => {
          const y = padding.top + (innerH * i) / gridYTicks;
          return (
            <line
              key={i}
              x1={padding.left}
              y1={y}
              x2={padding.left + innerW}
              y2={y}
              className="stroke-chart-grid"
              strokeWidth="1"
              strokeDasharray="2 3"
            />
          );
        })}

        {series.map((s) => {
          if (s.points.length === 0) return null;
          const sorted = [...s.points].sort(
            (a, b) =>
              new Date(a.bucket).getTime() - new Date(b.bucket).getTime(),
          );
          const points = sorted
            .map(
              (p) =>
                `${projectX(new Date(p.bucket).getTime()).toFixed(2)},${projectY(p.value).toFixed(2)}`,
            )
            .join(" ");
          return (
            <polyline
              key={s.name}
              points={points}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              className={SERIES_COLOR_CLASS[s.tone]}
            />
          );
        })}
      </svg>

      {/* x-axis timeline ticks */}
      <div className="relative h-4 text-[10px] text-fg-faint">
        {xTicks.map((t, i) => (
          <span
            key={i}
            className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{
              left: `${i === 0 ? 2 : i === xTicks.length - 1 ? 98 : t.pctLeft}%`,
            }}
          >
            {t.label}
          </span>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-fg-muted">
        {series.map((s) => (
          <span key={s.name} className="inline-flex items-center gap-1.5">
            <span
              className={`h-2 w-2 rounded-full ${LEGEND_DOT[s.tone]}`}
              aria-hidden="true"
            />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}
